import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Download, ExternalLink } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/images";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

type Sheet = {
  title: string;
  desc: string;
  category: "Solfilm invändig" | "Solfilm utvändig" | "Säkerhetsfilm" | "Broschyr";
  href: string;
  /** Dölj tillfälligt (t.ex. saknad komplett PDF). */
  hidden?: boolean;
};

const BASE = "/datablad";

const sheets: Sheet[] = [
  // ── Solfilm invändig ────────────────────────────────────────────────
  {
    title: "3M Prestige 70",
    desc: "Metallfri solfilm med nanoteknik, högt ljusgenomsläpp och spektralt selektiv solkontroll (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/3m-prestige-70-produktblad.pdf`,
  },
  {
    title: "3M Prestige 70 (produktblad)",
    desc: "Alternativt produktblad för 3M Prestige 70 (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/3m-prestige-70.pdf`,
  },
  {
    title: "3M Silver 20",
    desc: "Klassisk silverfilm för hög värme- och bländningsreduktion (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/3m-silver-20.pdf`,
  },
  {
    title: "3M Night Vision 15",
    desc: "Mörktonad film med låg interiör reflektion — bra sikt ut nattetid (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/3m-night-vision-15.pdf`,
  },
  {
    title: "3M Night Vision 25",
    desc: "Night Vision-serien, ljusare variant (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/3m-night-vision-25.pdf`,
  },
  {
    title: "3M Night Vision 35",
    desc: "Night Vision-serien, ljusaste varianten (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/nv35_se_000032-5.pdf`,
    // Endast trunkerad arkivkopia finns — dold tills en komplett PDF laddas upp.
    hidden: true,
  },
  {
    title: "3M Amber 35",
    desc: "Något ljusare än Silver 20, med en varmare ton. Populär på kontor (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/a35_se_000027-5.pdf`,
    // Endast trunkerad arkivkopia finns — dold tills en komplett PDF laddas upp.
    hidden: true,
  },
  {
    title: "3M Amber 35 Low E",
    desc: "Amber 35 Low E — framtagen för att bevara värmen inomhus vintertid (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/a35le_se_000026-5.pdf`,
    // Endast trunkerad arkivkopia finns — dold tills en komplett PDF laddas upp.
    hidden: true,
  },

  {
    title: "3M CC75 Isolering- och solfilm",
    desc: "3M CC75 Isolering- och solfilm är nästan osynlig på fönsterrutan.",
    category: "Solfilm invändig",
    href: `${BASE}/3m-cc75.pdf`,
  },
  {
    title: "Solar Screen Silver 70C",
    desc: "Kraftigt/medium reflekterande spegelfilm (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/solar-screen-silver-70c.pdf`,
  },
  {
    title: "Solar Screen Silver 50C",
    desc: "Lätt reflekterande spegelfilm (invändig).",
    category: "Solfilm invändig",
    href: `${BASE}/solar-screen-silver-50c.pdf`,
  },

  // ── Solfilm utvändig ────────────────────────────────────────────────
  {
    title: "3M Prestige 40 Exterior",
    desc: "Prestige för utsidan av glas där invändig montering inte är möjlig.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-prestige-40-exterior.pdf`,
  },
  {
    title: "3M Prestige 70 Exterior",
    desc: "Prestige för utvändig montering med högt ljusgenomsläpp.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-prestige-70-exterior.pdf`,
  },
  {
    title: "3M Prestige 90 Exterior",
    desc: "Utvändig Prestige, maximal klarhet.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-prestige-90-exterior.pdf`,
  },
  {
    title: "3M Silver 15 Exterior",
    desc: "Utvändig silverfilm — hög värmereduktion.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-silver-15-exterior.pdf`,
  },
  {
    title: "3M Silver 35 Exterior",
    desc: "Utvändig silverfilm — ljusare variant.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-silver-35-exterior.pdf`,
  },
  {
    title: "3M Neutral 35 Exterior",
    desc: "Neutral utvändig solfilm — bibehållen färgåtergivning.",
    category: "Solfilm utvändig",
    href: `${BASE}/3m-neutral-35-exterior.pdf`,
  },
  {
    title: "Solar Screen Silver 60 XC",
    desc: "Solar Screen utvändig solfilm — hållbar för utsatta ytor.",
    category: "Solfilm utvändig",
    href: `${BASE}/solar-screen-silver-60-xc.pdf`,
  },

  {
    title: "Solar Screen Silver 80XC",
    desc: "Kraftigt reflekterande spegelfilm (utvändig).",
    category: "Solfilm utvändig",
    href: `${BASE}/solar-screen-silver-80xc.pdf`,
  },
  {
    title: "Solar Screen Silver 285XC",
    desc: "Kraftigt reflekterande spegelfilm (utvändig).",
    category: "Solfilm utvändig",
    href: `${BASE}/solar-screen-silver-285xc.pdf`,
  },
  {
    title: "Solar Screen Silver 250XC",
    desc: "Lätt reflekterande spegelfilm (utvändig).",
    category: "Solfilm utvändig",
    href: `${BASE}/solar-screen-silver-250xc.pdf`,
  },
  {
    title: "Solar Screen Natural 260XC",
    desc: "Tonad medium reflekterande spegelfilm utan mycket spegling (utvändig).",
    category: "Solfilm utvändig",
    href: `${BASE}/solar-screen-natural-260xc.pdf`,
  },

  // ── Säkerhetsfilm ───────────────────────────────────────────────────
  {
    title: "3M Ultra Series (family card)",
    desc: "Klar, mikrolagrad säkerhetsfilm från 3M med EN 12600-klassning.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-ultra-series-family-card.pdf`,
  },
  {
    title: "3M Safety S40 (invändig)",
    desc: "Säkerhetsfilm med integrerad solfilm som ger både splitterskydd och solreduktion.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-safety-40-invandig.pdf`,
  },
  {
    title: "3M Safety S70 (invändig)",
    desc: "Ljusare variant av säkerhetsfilm med integrerad solfilm.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-safety-70.pdf`,
  },
  {
    title: "3M Safety S140",
    desc: "Klar säkerhetsfilm som ger splitterskydd utan att tona glaset.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-safety-s140.pdf`,
  },
  {
    title: "3M Safety S40 Exterior (utvändig)",
    desc: "Utvändig säkerhetsfilm med integrerad solreduktion.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-safety-40-utvandig.pdf`,
  },
  {
    title: "3M Säkerhetsfilm — broschyr",
    desc: "Översiktsbroschyr över hela 3M:s säkerhetsfilmssortiment.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-sakerhetsfilm-broschyr.pdf`,
  },

  {
    title: "3M Ultra S600",
    desc: "Ultra S600 är 3M’s flaggskepp bland de patenterade säkerhetsfilmerna.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-ultra-s600.pdf`,
  },
  {
    title: "3M Ultra Night Vision S25",
    desc: "Ultra NV S25 är en säkerhetsfilm som är kombinerad med 3M’s patenterade Night Vision-solfilm.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-ultra-night-vision-s25.pdf`,
  },
  {
    title: "3M Ultra PR S70",
    desc: "Ultra Prestige S70 är en unik patenterad säkerhetsfilm.",
    category: "Säkerhetsfilm",
    href: `${BASE}/3m-ultra-prestige-s70.pdf`,
  },
  {
    title: "Solar Screen Clear 4C",
    desc: "Säkerhetsfilmen håller ihop glaset om det skulle krossas (invändig).",
    category: "Säkerhetsfilm",
    href: `${BASE}/solar-screen-clear-4c.pdf`,
  },
  {
    title: "Solar Screen Clear 7C",
    desc: "Kraftig säkerhetsfilm som håller ihop glaset om det skulle krossas (invändig).",
    category: "Säkerhetsfilm",
    href: `${BASE}/solar-screen-clear-7c.pdf`,
  },
  {
    title: "Solar Screen Clear 8C",
    desc: "Kraftig säkerhetsfilm som håller ihop glaset om det skulle krossas (invändig).",
    category: "Säkerhetsfilm",
    href: `${BASE}/solar-screen-clear-8c.pdf`,
  },
  {
    title: "Solar Screen Clear 12C",
    desc: "Extra kraftig säkerhetsfilm som håller ihop glaset om det skulle krossas (invändig).",
    category: "Säkerhetsfilm",
    href: `${BASE}/solar-screen-clear-12c.pdf`,
  },
  {
    title: "Solar Screen Safe 4XC",
    desc: "Säkerhetsfilmen håller ihop glaset om det skulle krossas (utvändig).",
    category: "Säkerhetsfilm",
    href: `${BASE}/solar-screen-safe-4xc.pdf`,
  },

  // ── Broschyr ────────────────────────────────────────────────────────
  {
    title: "BBN Nordic — Inspirationsbroschyr",
    desc: "Referenser, tillämpningar och inspiration för dekor-, sol- och säkerhetsfilm.",
    category: "Broschyr",
    href: `${BASE}/bbn-nordic-inspiration.pdf`,
  },
];

export const Route = createFileRoute("/datablad")({
  head: () => ({
    meta: [
      { title: "Tekniska datablad — 3M & Solar Screen fönsterfilm | BBN Nordic" },
      { name: "description", content: "Ladda ner tekniska datablad för 3M Prestige, 3M Ultra, 3M Night Vision, Silver, Safety och Solar Screen — direkt från BBN Nordic." },
      { property: "og:title", content: "Tekniska datablad — fönsterfilm" },
      { property: "og:description", content: "PDF-datablad för 3M och Solar Screen solfilm, säkerhetsfilm och broschyr." },
      { property: "og:url", content: "https://bbnnordic.se/datablad" },
      { property: "og:image", content: IMAGES.installer },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/datablad" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Tekniska datablad — 3M & Solar Screen fönsterfilm",
          url: "/datablad",
          description:
            "Officiella tekniska datablad (PDF) för 3M Prestige, 3M Ultra, 3M Night Vision, Silver, Safety och Solar Screen.",
          isPartOf: { "@type": "WebSite", name: "BBN Nordic", url: "/" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Datablad", item: "https://bbnnordic.se/datablad" },
          ],

        }),
      },
    ],
  }),
  component: DatabladPage,
});

function DatabladPage() {
  const categories: Sheet["category"][] = [
    "Solfilm invändig",
    "Solfilm utvändig",
    "Säkerhetsfilm",
    "Broschyr",
  ];
  return (
    <>
      <PageHero
        eyebrow="TEKNISKA DATABLAD"
        title="Datablad & specifikationer"
        subtitle="Här finns officiella datablad för 3M och Solar Screen i vårt arkiv. PDF-filerna öppnas i en ny flik. Hittar du inte det du söker kan du kontakta oss, så skickar vi det."
        image={IMAGES.installer}
        breadcrumbs={[{ label: "Datablad" }]}
      />
      <PostHeroConversion />

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10 space-y-16">
          {categories.map((cat) => {
            const items = sheets.filter((s) => !s.hidden && s.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat}>
                <Reveal>
                  <p className="section-number">{cat.toUpperCase()}</p>
                  <h2 className="mt-3 text-2xl md:text-3xl">{cat} — datablad</h2>
                </Reveal>
                <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((s, i) => (
                    <Reveal key={s.title} delay={i * 0.05}>
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="glass p-6 h-full flex flex-col group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-gold/15 text-gold">
                            <FileText size={20} />
                          </span>
                          <ExternalLink size={16} className="text-muted-foreground" />
                        </div>
                        <h3 className="mt-5 text-lg font-semibold">{s.title}</h3>
                        <p className="mt-2 text-sm text-muted-foreground font-light flex-1">{s.desc}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                          Öppna PDF
                          <Download size={14} />
                        </span>
                      </a>
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}

          <Reveal>
            <div className="glass p-8 text-center">
              <h3 className="text-xl font-semibold">Saknar du ett datablad?</h3>
              <p className="mt-2 text-muted-foreground font-light">
                Berätta vilken produkt eller specifikation du söker, så hjälper vi dig att hitta databladet och skickar PDF:en.
              </p>
              <Link
                to="/kontakt"
                className="mt-5 inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded font-semibold btn-glow"
              >
                Kontakta oss
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
